import React from 'react';
import { ArrowLeft, ArrowRight, Layout, Type, Image, Palette, Plus, X } from 'lucide-react';
import { useCheckoutStore } from '../../store/checkoutStore';
import { useProductStore } from '../../store/productStore';
import { CheckoutPreview } from '../Checkout/CheckoutPreview';

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export const CheckoutEditor = ({ onNext, onBack }: Props) => {
  const { currentPage, updatePageSettings } = useCheckoutStore();
  const { getProduct } = useProductStore();
  const mainProduct = currentPage?.productId ? getProduct(currentPage.productId) : null;
  
  const [activeTab, setActiveTab] = React.useState('content');
  const [settings, setSettings] = React.useState({
    heading: 'Subscribe and start saving your money today!',
    features: [
      'All features included in basic plan',
      'Easy and flexible business management',
      'Full time customer support',
      '20 TB cloud storage'
    ],
    buttonText: 'Make payment',
    primaryColor: '#1E293B',
    logo: '',
  });

  const [newFeature, setNewFeature] = React.useState('');
  const [displayDetails, setDisplayDetails] = React.useState({
    name: currentPage?.displayName || mainProduct?.name || '',
    description: currentPage?.displayDescription || mainProduct?.description || '',
  });

  React.useEffect(() => {
    if (mainProduct && !currentPage?.displayName) {
      setDisplayDetails({
        name: mainProduct.name,
        description: mainProduct.description,
      });
      updatePageSettings({
        displayName: mainProduct.name,
        displayDescription: mainProduct.description,
      });
    }
  }, [mainProduct]);

  const handleSettingChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleDisplayChange = (key: string, value: string) => {
    setDisplayDetails(prev => ({ ...prev, [key]: value }));
    updatePageSettings({
      [key === 'name' ? 'displayName' : 'displayDescription']: value,
    });
  };

  const addFeature = (e: React.FormEvent) => {
    e.preventDefault();
    if (newFeature.trim()) {
      handleSettingChange('features', [...settings.features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    const newFeatures = settings.features.filter((_, i) => i !== index);
    handleSettingChange('features', newFeatures);
  };

  const moveFeature = (index: number, direction: 'up' | 'down') => {
    const newFeatures = [...settings.features];
    if (direction === 'up' && index > 0) {
      [newFeatures[index], newFeatures[index - 1]] = [newFeatures[index - 1], newFeatures[index]];
    } else if (direction === 'down' && index < newFeatures.length - 1) {
      [newFeatures[index], newFeatures[index + 1]] = [newFeatures[index + 1], newFeatures[index]];
    }
    handleSettingChange('features', newFeatures);
  };

  return (
    <div className="grid grid-cols-12 gap-8 h-[calc(100vh-13rem)] overflow-hidden">
      {/* Editor Panel */}
      <div className="col-span-4 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-100">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('content')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === 'content'
                  ? 'bg-[#1E293B] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Type className="w-4 h-4" />
              Content
            </button>
            <button
              onClick={() => setActiveTab('design')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === 'design'
                  ? 'bg-[#1E293B] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Palette className="w-4 h-4" />
              Design
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'content' ? (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main Heading
                </label>
                <input
                  type="text"
                  value={settings.heading}
                  onChange={(e) => handleSettingChange('heading', e.target.value)}
                  className="input-field"
                />
              </div>

              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Display Settings</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={displayDetails.name}
                      onChange={(e) => handleDisplayChange('name', e.target.value)}
                      className="input-field"
                      placeholder="Enter display name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Description
                    </label>
                    <textarea
                      value={displayDetails.description}
                      onChange={(e) => handleDisplayChange('description', e.target.value)}
                      className="input-field"
                      rows={3}
                      placeholder="Enter display description"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-4">Features & Benefits</h3>
                
                <form onSubmit={addFeature} className="mb-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      className="input-field flex-1"
                      placeholder="Add a new feature"
                    />
                    <button type="submit" className="btn">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </form>

                <div className="space-y-2">
                  {settings.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group"
                    >
                      <span className="text-sm text-gray-700">{feature}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => moveFeature(index, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveFeature(index, 'down')}
                          disabled={index === settings.features.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                        >
                          ↓
                        </button>
                        <button
                          onClick={() => removeFeature(index)}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => handleSettingChange('primaryColor', e.target.value)}
                  className="w-full h-10 rounded-lg cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  value={settings.logo}
                  onChange={(e) => handleSettingChange('logo', e.target.value)}
                  className="input-field"
                  placeholder="https://your-logo-url.com/logo.png"
                />
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t">
          <div className="flex justify-between">
            <button
              onClick={onBack}
              className="btn btn-secondary flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={onNext}
              className="btn flex items-center gap-2"
            >
              Continue
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Preview Panel */}
      <div className="col-span-8 bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100">
          <h3 className="text-sm font-medium text-gray-700">
            Live Preview
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="h-full">
            <CheckoutPreview settings={settings} />
          </div>
        </div>
      </div>
    </div>
  );
};